import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, ChevronRight, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

gsap.registerPlugin(ScrollTrigger);

const IndustriesHero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        '.industries-hero-line',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1 }
      );

      tl.fromTo(
        '.industries-hero-breadcrumb',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.6'
      );

      tl.fromTo(
        '.industries-hero-title',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.4'
      );

      tl.fromTo(
        '.industries-hero-subtitle',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        '-=0.4'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/fww10wje_Industries%20page%20header%20background.jpg"
          alt="Commercial buildings Bayside Melbourne"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,33,51,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(229,33,51,0.2)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 pt-24 relative z-10">
        <div className="max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="industries-hero-breadcrumb flex items-center gap-2 text-white/60 text-sm mb-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <ChevronRight className="w-4 h-4" />
            <span className="text-securelec-red">Industries</span>
          </nav>

          <div
            className="industries-hero-line w-24 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-6 origin-left rounded-full"
            style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }}
          />
          <h1 className="industries-hero-title text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
            Industries
          </h1>
          <p className="industries-hero-subtitle text-lg md:text-xl text-white/80 leading-relaxed drop-shadow-md">
            Tailored electrical and security solutions for residential and commercial clients across Bayside Melbourne.
          </p>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-securelec-light to-transparent" />
    </section>
  );
};

const IndustriesContent = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.industries-text',
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      gsap.fromTo(
        '.industries-image-block',
        { x: 60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const industries = [
    {
      title: "Residential",
      description: "We provide electrical and security solutions for homeowners looking to improve safety, functionality and convenience. Our residential services include CCTV installation, alarm systems, lighting upgrades, switchboard upgrades, EV charger installations and home automation solutions. Whether it's a renovation, new build or security upgrade, our team ensures all work is completed safely and to the highest standards."
    },
    {
      title: "Commercial Offices",
      description: "Securelec works with commercial offices to deliver reliable electrical infrastructure and modern security systems. Our services help businesses maintain safe work environments while improving operational efficiency through structured cabling, access control systems, CCTV monitoring and lighting solutions."
    },
    {
      title: "Dental & Medical Clinics",
      description: "Medical and dental clinics require specialised electrical systems that support sensitive equipment and strict compliance requirements. Securelec has experience delivering electrical solutions for dental practices and healthcare environments, ensuring equipment operates reliably while maintaining safety and compliance standards."
    },
    {
      title: "Retail & Hospitality",
      description: "Retail stores, cafés, restaurants and hospitality venues rely on dependable electrical systems to operate smoothly. We assist with lighting installations, switchboard upgrades, security cameras, alarm systems and electrical maintenance to ensure businesses can operate without disruption."
    },
    {
      title: "Warehouses & Industrial Facilities",
      description: "Industrial environments require robust electrical systems and reliable security infrastructure. Securelec provides electrical installations, lighting systems, CCTV, access control and data infrastructure that support warehouse operations, logistics facilities and industrial sites."
    },
    {
      title: "Builders & Developers",
      description: "We work closely with builders, developers and construction teams to deliver electrical and security installations across new developments and commercial projects. Our team understands construction timelines and site requirements, ensuring electrical infrastructure and security systems are installed efficiently and to specification."
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-securelec-light relative overflow-hidden">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-64 bg-gradient-to-b from-transparent via-securelec-red to-transparent" />

      <div className="container mx-auto px-6">
        {/* Back to Home Link */}
        <a 
          href="/" 
          className="industries-text inline-flex items-center gap-2 text-securelec-gray/60 hover:text-securelec-red transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </a>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Text Content */}
          <div>
            <span className="industries-text inline-flex items-center gap-2 text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
              <Building2 className="w-4 h-4" />
              Who We Work With
            </span>
            <h2 className="industries-text text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
              Industries We Work With
            </h2>
            <div className="industries-text w-16 h-1 bg-securelec-red mb-8" />
            
            <p className="industries-text text-lg text-securelec-gray/80 mb-6 leading-relaxed">
              Securelec works with a wide range of residential and commercial clients across Carrum and the wider Bayside region. Our team understands that each industry has unique electrical and security requirements, and we deliver tailored solutions that prioritise safety, reliability and long-term performance.
            </p>
            
            <p className="industries-text text-lg text-securelec-gray/80 mb-10 leading-relaxed">
              From small businesses to large commercial facilities, our experience allows us to deliver installations and upgrades that meet industry standards and operational needs.
            </p>

            {/* Industries Accordion */}
            <Accordion type="single" collapsible className="industries-text space-y-2">
              {industries.map((industry, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-gray-200 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-xl font-bold text-securelec-dark hover:text-securelec-red hover:no-underline py-5">
                    {industry.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-securelec-gray/70 leading-relaxed pb-5">
                    {industry.description}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Mini CTA */}
            <div className="industries-text mt-10">
              <Button
                className="bg-securelec-red hover:bg-securelec-red/90 text-white px-8 py-6 text-lg rounded-md font-semibold relative overflow-hidden group transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => window.location.href = '/get-a-quote'}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get a Quote
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="industries-image-block relative lg:sticky lg:top-32">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/oc4083bt_Securelec%20Industries%20page.jpg"
                alt="Commercial building electrical and security services Bayside Melbourne"
                className="w-full h-[550px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-securelec-dark/20 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-securelec-red/10 rounded-2xl -z-10" />
            <div className="absolute -top-6 -right-6 w-20 h-20 bg-securelec-red/20 rounded-xl -z-10" />
            
            {/* Badge */}
            <div className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-lg">
              <span className="text-xs text-securelec-gray/50 block">Serving</span>
              <span className="text-lg font-bold text-securelec-red">Bayside Melbourne</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { IndustriesHero, IndustriesContent };
